import React from 'react'
import { FormDateField, FormField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { NameDto } from 'api/Common/Types'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { DividendPaymentDataDto } from 'api/Trading-Accounts/Types/DividendsDto'
import { InstrumentDto } from 'api/Trading-Accounts/Types/InstrumentDto'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import moment from 'moment'
import { FormSubmitValues } from 'utils/formValidation'
import { utcToZonedTime } from 'utils/time-zone/helpers'

export interface AddEditDividendFormValues {
  id?: string
  eventSubTypeId: number
  eventTypeId: number
  brokerSymbol: string
  eventDate: string
  amount: number
  currencyId: string
  comment: string
}

const AddEditDividendFormUI: React.FC<FormikProps<AddEditDividendFormValues> & OuterProps> = (
  props
) => {
  const { handleSubmit, setSubmitting, isSubmitting, errors, setFieldValue, values } = props
  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && props.onClose()}>
      <Form>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{props.isEdit ? 'Edit Dividend' : 'Add Dividend'}</DialogTitle>
          </DialogHeader>

          <FormSelectField
            name='brokerSymbol'
            label='Broker Symbol'
            placeholder='Broker Symbol'
            value={values.brokerSymbol}
            options={props.brokerSymbols.map(({ symbol }) => ({ id: symbol, name: symbol }))}
            disabled={props.isEdit}
            allowClear
            showSearch
            error={errors.brokerSymbol}
            onChange={(value) => setFieldValue('brokerSymbol', value || '')}
          />
          <FormDateField
            name='eventDate'
            label='Event Date'
            dateFormatType='dd/MM/yyyy'
            isClearable
            error={errors.eventDate}
          />
          <FormField
            name='amount'
            label='Amount'
            type='number'
            min={0}
            step={0.01}
            dataTest='amount-field'
            error={errors.amount}
          />
          <FormSelectField
            name='currencyId'
            label='Currency'
            placeholder='Currency'
            value={values.currencyId}
            options={props.dividendCurrencies.map((currency) => ({
              id: currency.id,
              name: currency.id,
            }))}
            allowClear
            showSearch
            error={errors.currencyId}
            onChange={(value) => setFieldValue('currencyId', value || '')}
          />
          <FormSelectField
            name='eventTypeId'
            label='Event Type'
            value={values.eventTypeId}
            options={[
              { id: 0, name: 'None' },
              ...props.dividendTypes.map((type) => ({ id: type.id, name: type.name })),
            ]}
            error={errors.eventTypeId}
            onChange={(value) => setFieldValue('eventTypeId', Number(value) || 0)}
          />
          <FormSelectField
            name='eventSubTypeId'
            label='Event SubType'
            value={values.eventSubTypeId}
            options={[
              { id: 0, name: 'None' },
              ...props.dividendSubTypes.map((type) => ({ id: type.id, name: type.name })),
            ]}
            error={errors.eventSubTypeId}
            onChange={(value) => setFieldValue('eventSubTypeId', Number(value) || 0)}
          />

          <DialogFooter>
            <Button disabled={isSubmitting} type='button' variant='outline' onClick={props.onClose}>
              Cancel
            </Button>
            <Button disabled={isSubmitting} type='button' onClick={handleSubmitForm}>
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Form>
    </Dialog>
  )
}

interface OuterProps {
  dividend?: DividendPaymentDataDto
  permissions: PermissionDto[]
  brokerSymbols: InstrumentDto[]
  dividendCurrencies: WalletCurrencyDto[]
  dividendTypes: NameDto[]
  dividendSubTypes: NameDto[]
  isOpen?: boolean
  isEdit?: boolean
  onClose(): void

  onSubmit(values: FormSubmitValues<AddEditDividendFormValues>): Promise<void>
}

function dataToSubmit(
  values: AddEditDividendFormValues
): FormSubmitValues<AddEditDividendFormValues> {
  return {
    ...values,
    eventDate: values.eventDate ? moment.utc(values.eventDate).format() : null,
  }
}

export const AddEditDividendForm = withFormik<OuterProps, AddEditDividendFormValues>({
  mapPropsToValues: ({ dividend }) => {
    return {
      id: dividend?.id || '',
      brokerSymbol: dividend?.exchangeSymbol || '',
      eventDate: dividend?.eventDate ? utcToZonedTime(dividend.eventDate).toISOString() : '',
      amount: dividend?.amount || 0,
      currencyId: dividend?.currency.id || '',
      eventTypeId: Number(dividend?.eventType.id) || 0,
      eventSubTypeId: Number(dividend?.eventSubType.id) || 0,
      comment: dividend?.comment || '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddEditDividendFormValues> = {}
    if (!values.eventSubTypeId) errors.eventSubTypeId = 'required'
    if (!values.eventTypeId) errors.eventTypeId = 'required'
    if (!values.brokerSymbol) errors.brokerSymbol = 'required'
    if (!values.eventDate) errors.eventDate = 'required'
    if (!values.currencyId) errors.currencyId = 'required'
    return errors
  },
  enableReinitialize: true,
})(AddEditDividendFormUI)
